import React, { useCallback, useEffect, useState } from 'react';
import type { Team } from '../../types';
import { billingService, formatDay, yuan, type PaidOrder, type PayIntent } from './billingService';
import { useBillingStore } from './billingStore';
import { PayDialog } from './PayDialog';

interface SeatsPanelProps {
  team: Team;
}

const STATE_LABEL: Record<string, { text: string; className: string }> = {
  FREE: { text: '免费', className: 'bg-gray-100 text-gray-700' },
  PAID: { text: '已付费', className: 'bg-green-100 text-green-700' },
  GRACE: { text: '宽限中', className: 'bg-amber-100 text-amber-800' },
  READONLY: { text: '只读', className: 'bg-red-100 text-red-700' },
};

/** 团队管理里的「席位与计费」：人数、已购席位、到期日，所有者/管理员可购买和续费 */
export const SeatsPanel: React.FC<SeatsPanelProps> = ({ team }) => {
  const billing = useBillingStore(s => s.byTeam[team.id]);
  const pricing = useBillingStore(s => s.pricing);
  const load = useBillingStore(s => s.load);
  const [pay, setPay] = useState<PayIntent | null>(null);
  const [orders, setOrders] = useState<PaidOrder[]>([]);

  const reloadOrders = useCallback(async () => {
    try {
      setOrders(await billingService.listOrders(team.id));
    } catch {
      setOrders([]); // 非所有者/管理员读不到，正常
    }
  }, [team.id]);

  useEffect(() => { load(team.id); }, [load, team.id]);
  useEffect(() => { if (billing?.canManage) reloadOrders(); }, [billing?.canManage, reloadOrders]);

  if (!billing) return null;

  const label = STATE_LABEL[billing.state];
  const hasPaid = billing.activePaidSeats > 0 || !!billing.paidUntil;
  const needMore = billing.seatsNeeded > billing.activePaidSeats;

  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-800">席位与计费</h3>
        <span className={`px-2 py-0.5 rounded-full text-xs ${label.className}`}>{label.text}</span>
      </div>

      <div className="text-sm text-gray-700">
        真人成员 <b>{billing.memberCount}</b> / 容量 <b>{billing.capacity}</b>
        <span className="text-gray-400">（免费 {billing.freeSeats} + 已购 {billing.activePaidSeats}）</span>
      </div>
      <div className="mt-1 text-xs text-gray-500">
        {billing.paidUntil
          ? `付费席位到期：${formatDay(billing.paidUntil)}${billing.inPaidGrace ? `，宽限到 ${formatDay(billing.graceEndsAt)}` : ''}`
          : `超出 ${pricing.freeSeats} 人后，每人每年 ${yuan(pricing.pricePerSeatCents)}（约 $${pricing.pricePerSeatUsdApprox}）`}
        {billing.inLaunchGrace && !billing.inPaidGrace && billing.graceEndsAt && `；免费宽限到 ${formatDay(billing.graceEndsAt)}`}
      </div>
      <div className="mt-1 text-xs text-gray-400">agent 成员不占席位，不收费</div>

      {billing.canManage ? (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => setPay('BUY_SEATS')}
            className="text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700"
          >
            {hasPaid ? '增购席位' : '购买席位'}
          </button>
          {hasPaid && (
            <button
              type="button"
              onClick={() => setPay('RENEW')}
              className="text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-white"
            >
              续费一年
            </button>
          )}
        </div>
      ) : (
        needMore && <div className="mt-3 text-xs text-gray-500">席位不足，联系团队所有者购买</div>
      )}

      {billing.canManage && orders.length > 0 && (
        <div className="mt-3 border-t border-gray-200 pt-2">
          <div className="text-xs text-gray-400 mb-1">付款记录</div>
          <ul className="space-y-0.5">
            {orders.slice(0, 5).map(o => (
              <li key={o.id} className="flex justify-between text-xs text-gray-600">
                <span>{formatDay(o.paidAt)} · {o.description}</span>
                <span>{yuan(o.amountCents)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pay && (
        <PayDialog
          teamId={team.id}
          intent={pay}
          defaultSeats={Math.max(1, billing.seatsNeeded - billing.activePaidSeats)}
          pricing={pricing}
          mock={billing.mock}
          onClose={() => setPay(null)}
          onPaid={() => { load(team.id); reloadOrders(); }}
        />
      )}
    </div>
  );
};
